/**
 * Account Utility Functions
 *
 * Centralizes account number extraction and comparison to handle
 * various object shapes and field naming conventions from API/SignalR.
 *
 * @module utils/accountUtils
 */

// ============================================================================
// ACCOUNT NUMBER EXTRACTION
// ============================================================================

/**
 * Extracts account number from various object shapes or primitive values
 *
 * Handles:
 * - Object with accountNumber: { accountNumber: '123456' }
 * - Object with AccountNumber: { AccountNumber: '123456' }
 * - Object with account_number: { account_number: '123456' }
 * - Primitive string: '123456'
 * - Primitive number: 123456
 *
 * @param {Object|string|number|null|undefined} account - Account data
 * @returns {string|null} Account number as string, or null if not found
 *
 * @example
 * getAccountNumber({ accountNumber: '123456' })  // '123456'
 * getAccountNumber({ AccountNumber: 123456 })    // '123456'
 * getAccountNumber('123456')                      // '123456'
 * getAccountNumber(123456)                        // '123456'
 * getAccountNumber(null)                          // null
 */
export const getAccountNumber = (account) => {
    // Handle null/undefined
    if (account === null || account === undefined) {
        return null;
    }

    // Handle primitive string
    if (typeof account === 'string') {
        return account.trim() || null;
    }

    // Handle primitive number
    if (typeof account === 'number') {
        return String(account);
    }

    // Handle object - try multiple field names
    if (typeof account === 'object') {
        const value = account.accountNumber ||
                     account.AccountNumber ||
                     account.account_number ||
                     account.accountNum ||
                     account.AccNum ||
                     null;

        if (value === null || value === undefined) {
            return null;
        }

        // Convert to string
        return String(value);
    }

    return null;
};

// ============================================================================
// ACCOUNT COMPARISON
// ============================================================================

/**
 * Compares two account identifiers for equality
 *
 * Handles type coercion (string vs number) and various object shapes.
 * Both values are normalized to strings before comparison.
 *
 * @param {Object|string|number|null|undefined} account1 - First account
 * @param {Object|string|number|null|undefined} account2 - Second account
 * @returns {boolean} True if both represent the same account
 *
 * @example
 * accountsMatch('123456', 123456)                    // true
 * accountsMatch({ accountNumber: '123' }, '123')    // true
 * accountsMatch({ AccountNumber: 123 }, { account_number: '123' })  // true
 * accountsMatch(null, '123')                         // false
 */
export const accountsMatch = (account1, account2) => {
    const num1 = getAccountNumber(account1);
    const num2 = getAccountNumber(account2);

    // If either is null, they don't match
    if (num1 === null || num2 === null) {
        return false;
    }

    // Compare as strings
    return num1 === num2;
};

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validates if a value contains a valid account number
 *
 * @param {Object|string|number|null|undefined} account - Value to validate
 * @returns {boolean} True if valid account number can be extracted
 *
 * @example
 * isValidAccount({ accountNumber: '123456' })  // true
 * isValidAccount('123456')                      // true
 * isValidAccount(null)                          // false
 * isValidAccount({})                            // false
 */
export const isValidAccount = (account) => {
    const accountNumber = getAccountNumber(account);
    return accountNumber !== null && accountNumber.length > 0;
};

/**
 * Validates account number format (6 digits)
 *
 * @param {Object|string|number|null|undefined} account - Value to validate
 * @returns {boolean} True if account number is exactly 6 digits
 */
export const isValidAccountFormat = (account) => {
    const accountNumber = getAccountNumber(account);
    if (!accountNumber) {
        return false;
    }

    // Check if exactly 6 digits
    return /^\d{6}$/.test(accountNumber);
};

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Formats account number for display (with masking option)
 *
 * @param {Object|string|number|null|undefined} account - Account data
 * @param {Object} options - Formatting options
 * @param {boolean} options.mask - If true, masks middle digits (default: false)
 * @returns {string} Formatted account number or placeholder
 *
 * @example
 * formatAccountNumber('123456')                    // '123456'
 * formatAccountNumber('123456', { mask: true })   // '12**56'
 * formatAccountNumber(null)                        // '--'
 */
export const formatAccountNumber = (account, options = {}) => {
    const accountNumber = getAccountNumber(account);

    if (!accountNumber) {
        return '--';
    }

    if (options.mask && accountNumber.length >= 4) {
        const start = accountNumber.slice(0, 2);
        const end = accountNumber.slice(-2);
        const middle = '*'.repeat(accountNumber.length - 4);
        return `${start}${middle}${end}`;
    }

    return accountNumber;
};

/**
 * Gets last N digits of account number (for display in compact spaces)
 *
 * @param {Object|string|number|null|undefined} account - Account data
 * @param {number} digits - Number of digits to return (default: 4)
 * @returns {string} Last N digits or placeholder
 *
 * @example
 * getAccountLastDigits('123456')      // '3456'
 * getAccountLastDigits('123456', 2)   // '56'
 */
export const getAccountLastDigits = (account, digits = 4) => {
    const accountNumber = getAccountNumber(account);

    if (!accountNumber) {
        return '--';
    }

    return accountNumber.slice(-digits);
};

// ============================================================================
// FILTERING HELPERS
// ============================================================================

/**
 * Filters an array of items by account number
 *
 * @param {Array} items - Array of items with account field
 * @param {Object|string|number} targetAccount - Account to filter by
 * @param {string} accountField - Field name containing account (default: 'accountNumber')
 * @returns {Array} Filtered items matching the account
 *
 * @example
 * filterByAccount(positions, selectedAccount)
 * filterByAccount(strategies, '123456', 'AccountNumber')
 */
export const filterByAccount = (items, targetAccount, accountField = 'accountNumber') => {
    if (!Array.isArray(items) || !targetAccount) {
        return [];
    }

    const targetNum = getAccountNumber(targetAccount);
    if (!targetNum) {
        return [];
    }

    return items.filter(item => {
        const itemAccount = item[accountField] ||
                           item.accountNumber ||
                           item.AccountNumber ||
                           item.account_number;
        return getAccountNumber(itemAccount) === targetNum;
    });
};

// ============================================================================
// EXPORTS SUMMARY
// ============================================================================

export default {
    // Extraction
    getAccountNumber,

    // Comparison
    accountsMatch,

    // Validation
    isValidAccount,
    isValidAccountFormat,

    // Display
    formatAccountNumber,
    getAccountLastDigits,

    // Filtering
    filterByAccount
};
